import React, { useState } from 'react';
import { Box, Button, Heading, Stack, Text } from '@indeed/ifl-components';

// ── Config ──────────────────────────────────────────────────────────────────

export interface ProctorOption {
    /** Proctor test name (matches src/server/proctor/specifications). */
    name: string;
    /** Lowest selectable bucket value (typically -1 = inactive). */
    min: number;
    /** Highest selectable bucket value. */
    max: number;
    /** Default bucket value when the page loads. */
    defaultValue: number;
    /** Human-readable bucket legend shown under the field. */
    buckets: string;
}

// Proctor buckets that meaningfully change the Auto Apply / setup experience.
// Values >= 0 are appended to generated links as `prforceGroups`; -1 (inactive)
// is omitted, so a link only forces the buckets you explicitly opt into.
export const SUPPORTED_PROCTORS: ProctorOption[] = [
    {
        name: 'auto_apply_main_tog',
        min: -1,
        max: 1,
        defaultValue: 1,
        buckets: '-1 inactive · 0 control · 1 rba',
    },
    {
        name: 'rnhp_auto_apply_banner_tst',
        min: -1,
        max: 2,
        defaultValue: -1,
        buckets: '-1 inactive · 0 control · 1 test · 2 progressive_automation',
    },
    {
        name: 'auto_apply_rebranding_tst',
        min: -1,
        max: 1,
        defaultValue: -1,
        buckets: '-1 inactive · 0 control · 1 test (Apply For Me)',
    },
    {
        name: 'apply_for_me_sfm_tog',
        min: -1,
        max: 1,
        defaultValue: -1,
        buckets: '-1 inactive · 0 disabled · 1 enabled',
    },
    {
        name: 'ia_auto_apply_preference_m1_tst',
        min: -1,
        max: 1,
        defaultValue: -1,
        buckets: '-1 inactive · 0 control · 1 test',
    },
    {
        name: 'js_pro_auto_apply_sq_prefill_tst',
        min: -1,
        max: 5,
        defaultValue: -1,
        buckets: '-1 inactive · 0 control · 1 test · 2-5 reserved',
    },
    {
        name: 'auto_apply_setup_review_contact_info',
        min: -1,
        max: 1,
        defaultValue: -1,
        buckets: '-1 inactive · 0 control · 1 test',
    },
    {
        name: 'ia_phone_number_verification_tst',
        min: -1,
        max: 1,
        defaultValue: 0,
        buckets: '-1 inactive · 0 control (verification off) · 1 test',
    },
    {
        name: 'ia_rbs_relax_sq_requirement_tst',
        min: -1,
        max: 1,
        defaultValue: -1,
        buckets: '-1 inactive · 0 control · 1 test (relax SQ requirement)',
    },
];

/** A proctor added ad-hoc via the panel (not part of SUPPORTED_PROCTORS). */
export interface CustomProctor {
    name: string;
    value: number;
}

/** Selectable bucket values offered for custom proctors. -1 leaves it unforced. */
export const CUSTOM_PROCTOR_VALUE_OPTIONS = [-1, 0, 1, 2, 3, 4, 5];

/** Build a `prforceGroups` value (e.g. `auto_apply_main_tog1,rnhp_auto_apply_banner_tst2`)
 * from the selected buckets, including only forced (>= 0) values. */
export const buildProctorParam = (
    values: Record<string, number>,
    options: ProctorOption[] = SUPPORTED_PROCTORS,
): string =>
    options
        .map((proctor) => ({ name: proctor.name, value: values[proctor.name] }))
        .filter((proctor) => Number.isFinite(proctor.value) && proctor.value >= 0)
        .map((proctor) => `${proctor.name}${proctor.value}`)
        .join(',');

// ── Hook ──────────────────────────────────────────────────────────────────────

export interface ProctorOverridesController {
    options: ProctorOption[];
    proctorValues: Record<string, number>;
    setProctorValue: (name: string, raw: string) => void;
    /** Ad-hoc proctors added via the panel, on top of `options`. */
    customProctors: CustomProctor[];
    /** Add (or, if the name already exists, update) a custom proctor. */
    setCustomProctor: (name: string, value: number) => void;
    removeCustomProctor: (name: string) => void;
    /** Comma-joined forced buckets, ready to drop into a `prforceGroups` param. */
    prforceGroups: string;
}

/** Owns the editable bucket state for a proctor-override panel and derives the
 * `prforceGroups` string. Shared by the test pages so both stay in sync. */
export const useProctorOverrides = (
    options: ProctorOption[] = SUPPORTED_PROCTORS,
): ProctorOverridesController => {
    const [proctorValues, setProctorValues] = useState<Record<string, number>>(() =>
        Object.fromEntries(options.map((proctor) => [proctor.name, proctor.defaultValue])),
    );
    const [customProctors, setCustomProctors] = useState<CustomProctor[]>([]);

    const setProctorValue = (name: string, raw: string) => {
        const parsed = parseInt(raw, 10);
        setProctorValues((prev) => ({ ...prev, [name]: Number.isNaN(parsed) ? -1 : parsed }));
    };

    const setCustomProctor = (name: string, value: number) => {
        const trimmed = name.trim();
        // Ignore blanks and names that would shadow a predefined proctor.
        if (!trimmed || options.some((proctor) => proctor.name === trimmed)) return;
        setCustomProctors((prev) => [
            ...prev.filter((proctor) => proctor.name !== trimmed),
            { name: trimmed, value },
        ]);
    };

    const removeCustomProctor = (name: string) => {
        setCustomProctors((prev) => prev.filter((proctor) => proctor.name !== name));
    };

    const customParam = customProctors
        .filter((proctor) => Number.isFinite(proctor.value) && proctor.value >= 0)
        .map((proctor) => `${proctor.name}${proctor.value}`)
        .join(',');

    const prforceGroups = [buildProctorParam(proctorValues, options), customParam]
        .filter(Boolean)
        .join(',');

    return {
        options,
        proctorValues,
        setProctorValue,
        customProctors,
        setCustomProctor,
        removeCustomProctor,
        prforceGroups,
    };
};

// ── Panel ──────────────────────────────────────────────────────────────────────

interface ProctorOverridesPanelProps {
    controller: ProctorOverridesController;
    /** Short blurb explaining where the forced buckets end up. */
    description?: React.ReactNode;
}

/** Sticky side panel of number fields, one per supported proctor, plus a live
 * `prforceGroups` preview. Presentational — state lives in `useProctorOverrides`. */
export const ProctorOverridesPanel: React.FC<ProctorOverridesPanelProps> = ({
    controller,
    description,
}) => {
    const {
        options,
        proctorValues,
        setProctorValue,
        customProctors,
        setCustomProctor,
        removeCustomProctor,
        prforceGroups,
    } = controller;

    // Local state for the "add proctor" form.
    const [newName, setNewName] = useState('');
    const [newValue, setNewValue] = useState(1);

    const handleAdd = () => {
        setCustomProctor(newName, newValue);
        setNewName('');
        setNewValue(1);
    };

    return (
        <Box
            sx={{
                flex: '0 0 300px',
                position: 'sticky',
                insetBlockStart: 8,
                marginBlockStart: 8,
                padding: 4,
                border: '1px solid',
                borderColor: 'border.weak',
                borderRadius: 'md',
                backgroundColor: 'background.weaker',
                fontFamily: 'mono',
            }}
        >
            <Heading level={3} sx={{ marginBlockEnd: 2 }}>
                Proctor overrides
            </Heading>
            <Text variant="secondary" sx={{ marginBlockEnd: 4, display: 'block', fontSize: 'sm' }}>
                {description ?? (
                    <>
                        Forced buckets are appended to the generated links as{' '}
                        <code>prforceGroups</code>. Set a proctor to <code>-1</code> to leave it
                        unforced.
                    </>
                )}
            </Text>

            <Stack spacing={4} vertical>
                {options.map((proctor) => (
                    <Box
                        key={proctor.name}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 3,
                        }}
                    >
                        <Box sx={{ minInlineSize: 0 }}>
                            <label
                                htmlFor={`proctor-${proctor.name}`}
                                style={{
                                    display: 'block',
                                    fontFamily: 'monospace',
                                    fontSize: '13px',
                                    fontWeight: 700,
                                    wordBreak: 'break-all',
                                }}
                            >
                                {proctor.name}
                            </label>
                            <Text
                                variant="secondary"
                                sx={{ display: 'block', fontSize: 'xs', marginBlockStart: 1 }}
                            >
                                {proctor.buckets}
                            </Text>
                        </Box>
                        <input
                            id={`proctor-${proctor.name}`}
                            type="number"
                            min={proctor.min}
                            max={proctor.max}
                            step={1}
                            value={proctorValues[proctor.name]}
                            onChange={(e) => setProctorValue(proctor.name, e.target.value)}
                            style={{
                                width: '64px',
                                flexShrink: 0,
                                fontFamily: 'monospace',
                                fontSize: '13px',
                            }}
                        />
                    </Box>
                ))}

                {customProctors.map((proctor) => (
                    <Box
                        key={proctor.name}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 3,
                        }}
                    >
                        <Box sx={{ minInlineSize: 0 }}>
                            <Text
                                sx={{
                                    display: 'block',
                                    fontFamily: 'mono',
                                    fontSize: 'sm',
                                    fontWeight: '700',
                                    wordBreak: 'break-all',
                                }}
                            >
                                {proctor.name}
                            </Text>
                            <Text
                                variant="secondary"
                                sx={{ display: 'block', fontSize: 'xs', marginBlockStart: 1 }}
                            >
                                custom · -1 unforced
                            </Text>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
                            <select
                                aria-label={`${proctor.name} value`}
                                value={proctor.value}
                                onChange={(e) =>
                                    setCustomProctor(proctor.name, parseInt(e.target.value, 10))
                                }
                                style={{ fontFamily: 'monospace', fontSize: '13px' }}
                            >
                                {CUSTOM_PROCTOR_VALUE_OPTIONS.map((value) => (
                                    <option key={value} value={value}>
                                        {value}
                                    </option>
                                ))}
                            </select>
                            <button
                                type="button"
                                aria-label={`Remove ${proctor.name}`}
                                onClick={() => removeCustomProctor(proctor.name)}
                                style={{
                                    fontFamily: 'monospace',
                                    fontSize: '13px',
                                    cursor: 'pointer',
                                    lineHeight: 1,
                                }}
                            >
                                ×
                            </button>
                        </Box>
                    </Box>
                ))}
            </Stack>

            <Box
                sx={{
                    marginBlockStart: 4,
                    paddingBlockStart: 3,
                    borderBlockStart: '1px solid',
                    borderColor: 'border.weak',
                }}
            >
                <Text
                    sx={{ display: 'block', fontSize: 'sm', fontWeight: '700', marginBlockEnd: 2 }}
                >
                    Add proctor
                </Text>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <input
                        aria-label="New proctor name"
                        type="text"
                        value={newName}
                        placeholder="proctor_name"
                        onChange={(e) => setNewName(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') handleAdd();
                        }}
                        style={{
                            flex: '1 1 auto',
                            minInlineSize: 0,
                            fontFamily: 'monospace',
                            fontSize: '13px',
                        }}
                    />
                    <select
                        aria-label="New proctor value"
                        value={newValue}
                        onChange={(e) => setNewValue(parseInt(e.target.value, 10))}
                        style={{ flexShrink: 0, fontFamily: 'monospace', fontSize: '13px' }}
                    >
                        {CUSTOM_PROCTOR_VALUE_OPTIONS.map((value) => (
                            <option key={value} value={value}>
                                {value}
                            </option>
                        ))}
                    </select>
                    <Button
                        variant="secondary"
                        size="sm"
                        disabled={!newName.trim()}
                        onClick={handleAdd}
                    >
                        Add
                    </Button>
                </Box>
            </Box>

            <Box
                sx={{
                    marginBlockStart: 4,
                    paddingBlockStart: 3,
                    borderBlockStart: '1px solid',
                    borderColor: 'border.weak',
                }}
            >
                <Text sx={{ display: 'block', fontSize: 'sm', fontWeight: '700' }}>
                    prforceGroups
                </Text>
                <Text
                    variant="secondary"
                    data-testid="proctor-prforce-preview"
                    sx={{ display: 'block', fontSize: 'sm', wordBreak: 'break-all' }}
                >
                    {prforceGroups || '(none forced)'}
                </Text>
            </Box>
        </Box>
    );
};
